import {
  BadgeCheck,
  Ban,
  CircleX,
  Clock,
  Gift,
  Handshake,
  Hourglass,
  Link2,
  RefreshCw,
  ShieldCheck,
  ShoppingBag,
  TriangleAlert,
  Undo2,
  UserPlus,
  Users,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { animatedIcon } from '@/shared/components/animated-icon';
import type { MgmRole, ReferralStatus, RewardStatus } from '../model/types';

/**
 * Ícones animados do Indique e Ganhe, criados UMA vez (escopo de módulo) para
 * não remontar a cada render. Tocam ao aparecer e no hover — sem loop, porque
 * aparecem em listas.
 */
export const REFERRAL_ICON: Record<ReferralStatus, LucideIcon> = {
  indicated: animatedIcon(Link2, 'pop'),
  registered: animatedIcon(UserPlus, 'pop'),
  converted: animatedIcon(ShoppingBag, 'bounce'),
  rejected: animatedIcon(Ban, 'shake'),
};

export const REWARD_ICON: Record<RewardStatus, LucideIcon> = {
  pending: animatedIcon(Hourglass, 'flip'),
  granted: animatedIcon(Gift, 'wiggle'),
  reversed: animatedIcon(Undo2, 'pop'),
  cancelled: animatedIcon(CircleX, 'pop'),
  failed: animatedIcon(TriangleAlert, 'pulse'),
};

/** Papel na indicação: quem indica ganha um presente; quem é indicado, o aperto de mão. */
export const ROLE_ICON: Record<MgmRole, LucideIcon> = {
  referrer: Gift,
  referred: Handshake,
};

/** Gatilho/validação no formulário da campanha. */
export const TRIGGER_ICON = {
  registered: UserPlus,
  first_purchase: ShoppingBag,
  hold: Hourglass,
  immediate: Zap,
} as const;

/** Ícones dos KPIs (relatório e aba do lead). */
export const METRIC_ICON = {
  referrers: animatedIcon(Users, 'pop'),
  indicated: animatedIcon(Link2, 'pop'),
  registered: animatedIcon(UserPlus, 'bounce'),
  converted: animatedIcon(ShoppingBag, 'bounce'),
  rewards: animatedIcon(Gift, 'wiggle'),
} as const;

/**
 * Eventos da timeline de uma indicação (`events[]` gravado pelo backend):
 * ícone + tom (cor do círculo). Tipo desconhecido cai no neutro.
 */
export type EventTone = 'info' | 'accent' | 'success' | 'warning' | 'danger' | 'neutral';

export const EVENT_ICON: Record<string, { icon: LucideIcon; tone: EventTone }> = {
  indicated: { icon: Link2, tone: 'info' },
  reassigned: { icon: RefreshCw, tone: 'neutral' },
  campaign_switched: { icon: RefreshCw, tone: 'neutral' },
  registered: { icon: UserPlus, tone: 'accent' },
  rejected: { icon: Ban, tone: 'danger' },
  purchase_detected: { icon: ShoppingBag, tone: 'success' },
  converted: { icon: BadgeCheck, tone: 'success' },
  purchase_cancelled: { icon: Undo2, tone: 'warning' },
  conversion_reverted: { icon: Undo2, tone: 'warning' },
  purchase_window_expired: { icon: Clock, tone: 'neutral' },
  clawback_window_closed: { icon: ShieldCheck, tone: 'neutral' },
  reward_granted: { icon: Gift, tone: 'success' },
  reward_reversed: { icon: Undo2, tone: 'danger' },
  reward_cancelled: { icon: CircleX, tone: 'neutral' },
};

export const EVENT_FALLBACK: { icon: LucideIcon; tone: EventTone } = { icon: Clock, tone: 'neutral' };
